import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, money, COURSE_IMAGES, formatApiErrorDetail } from "../lib/data";
import { useAuth } from "../context/AuthContext";
import { Copy, Bitcoin, Check, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const { courseId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [btc, setBtc] = useState("");
  const [txid, setTxid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourse(r.data.find((c) => c.id === courseId)));
    api.get("/config").then((r) => setBtc(r.data.btc_address));
  }, [courseId]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { state: { from: `/checkout/${courseId}` } });
  }, [authLoading, user, courseId, navigate]);

  const copy = () => {
    navigator.clipboard.writeText(btc);
    setCopied(true);
    toast.success("Bitcoin address copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/purchases", { course_id: courseId, txid });
      toast.success("Payment submitted! Awaiting admin approval.");
      navigate("/members");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (!course) return (<div><Navbar /><div className="max-w-3xl mx-auto px-6 py-20">Loading…</div></div>);

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-10">
        {/* Order summary */}
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-3">Checkout</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0A1914] mb-6">{course.title}</h1>
          <img src={COURSE_IMAGES[course.id]} alt={course.title} className="w-full h-48 object-cover border border-gray-200 mb-6" />
          <p className="text-sm text-gray-600 mb-6">{course.description}</p>
          <div className="border border-gray-200 divide-y divide-gray-200">
            <div className="flex justify-between px-4 py-3"><span className="text-sm text-gray-600">Course price</span><span className="font-mono text-[#0A1914]">{money(course.price)}</span></div>
            <div className="flex justify-between px-4 py-3"><span className="text-sm text-gray-600">Weekly payout to you</span><span className="font-mono text-[#00B368]">{money(course.weekly)} × 52</span></div>
            <div className="flex justify-between px-4 py-3 bg-[#F3F4F3]"><span className="text-sm font-semibold">Total 52-week payout</span><span className="font-mono font-semibold text-[#00B368]">{money(course.weekly * 52)}</span></div>
          </div>
        </div>

        {/* Payment */}
        <div className="border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bitcoin className="h-5 w-5 text-[#F7931A]" />
            <h2 className="font-display text-xl text-[#0A1914]">Pay with Bitcoin</h2>
          </div>
          <p className="text-sm text-gray-600 mb-5">Send exactly <span className="font-mono text-[#0A1914]">{money(course.price)}</span> worth of BTC to the address below, then paste your transaction ID (TXID) to confirm.</p>

          <div className="bg-white border border-gray-200 p-4 flex justify-center mb-4">
            {btc && <QRCode value={`bitcoin:${btc}`} size={160} fgColor="#0A1914" />}
          </div>

          <label className="text-xs font-mono uppercase tracking-wider text-gray-500">BTC Address</label>
          <div className="flex items-center gap-2 mt-1 mb-5">
            <code data-testid="btc-address" className="flex-1 font-mono text-xs bg-[#F3F4F3] border border-gray-200 px-3 py-2.5 break-all">{btc}</code>
            <button onClick={copy} data-testid="copy-btc" className="shrink-0 border border-[#0A1914] p-2.5 rounded-sm hover:bg-[#F3F4F3] transition-colors">
              {copied ? <Check className="h-4 w-4 text-[#00B368]" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <form onSubmit={submit}>
            <label className="text-xs font-mono uppercase tracking-wider text-gray-500">Transaction ID (TXID)</label>
            <input data-testid="txid-input" required value={txid} onChange={(e) => setTxid(e.target.value)} placeholder="Paste your Bitcoin transaction ID" className="w-full mt-1 mb-4 font-mono text-sm border border-gray-300 rounded-sm px-3 py-2.5 focus:border-[#00B368] focus:ring-1 focus:ring-[#00B368]/20 outline-none" />
            <button data-testid="submit-payment" disabled={submitting} className="w-full bg-[#0A1914] text-white py-3 rounded-sm hover:bg-[#00B368] transition-colors disabled:opacity-50">{submitting ? "Submitting…" : "Submit payment for approval"}</button>
          </form>
          <p className="flex items-center gap-2 text-xs text-gray-500 mt-4"><ShieldCheck className="h-3.5 w-3.5 text-[#00B368]" /> An admin verifies your payment. You get instant course access on approval.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
